import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
    Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Select, MenuItem, CircularProgress, Link, IconButton, useTheme,
} from '@mui/material'
import { Copy, Trash2 } from 'lucide-react'

import { listReports, generateReport, revokeReport } from '../../../../services/api/AnalyticsDataService'
import { SectionPaper, todayISO, daysAgoISO } from './analyticsUi'

export default function SponsorReportsSection({ pageId, links, locked }) {
    const theme = useTheme()
    const pink = theme.palette.primary.main
    const [open, setOpen] = useState(false)

    const { data: reportsRaw = [], isLoading, refetch } = useQuery({
        queryKey: ['sponsor-reports'],
        queryFn: listReports,
        enabled: !locked,
    })
    // Deleting a report soft-revokes it on the backend; we simply hide revoked ones so the row vanishes.
    const reports = reportsRaw.filter((r) => !r.revokedAt)

    if (locked) {
        return (
            <SectionPaper title="Brand Reports">
                <Typography sx={{ fontSize: '.92rem', color: '#555' }}>
                    Generate shareable reports brands can trust with Pro.
                </Typography>
            </SectionPaper>
        )
    }

    return (
        <>
            <SectionPaper>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                        <Typography variant="h6" component="h6" sx={{ fontWeight: 600, fontFamily: 'inherit' }}>Brand Reports</Typography>
                        <Typography sx={{ fontSize: '.88rem', color: '#777' }}>
                            Pick a date range and your link to generate a report that you can share.
                        </Typography>
                    </Box>
                    <Button onClick={() => setOpen(true)} variant="contained"
                        sx={{ width: { xs: '100%', sm: 'auto' }, p: '.4rem 2rem', whiteSpace: 'nowrap', textTransform: 'none', borderRadius: '40px', background: pink, '&:hover': { background: pink, filter: 'brightness(0.92)' } }}>
                        Generate Report
                    </Button>
                </Box>

                <Typography sx={{ fontWeight: 600, fontFamily: 'inherit', mt: 3, mb: 1 }}>
                    Previous reports
                </Typography>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
                ) : reports.length === 0 ? (
                    <Typography sx={{ color: '#aaa', fontSize: '.9rem' }}>
                        your reports will be here for you to access anytime. generate one now to start
                    </Typography>
                ) : (
                    reports.map((r) => (
                        <Box key={r.reportId} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.5, sm: 2 }, py: 1.2, borderTop: '1px solid #f0f0f0' }}>
                            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                                <Typography noWrap sx={{ fontWeight: 600, color: '#222' }}>{r.campaignName}</Typography>
                                <Typography sx={{ fontSize: '.8rem', color: '#888' }}>
                                    {r.rangeStart} – {r.rangeEnd} · {(r.focusLinkClicks || 0).toLocaleString()} clicks
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Link href={r.shareUrl} target="_blank" rel="noreferrer"
                                    sx={{ fontSize: '1rem', color: pink, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                                    View ↗
                                </Link>
                                <IconButton size="small" title="Delete"
                                    onClick={async () => { await revokeReport(r.reportId); refetch() }}>
                                    <Trash2 size={14} />
                                </IconButton>
                            </Box>
                        </Box>
                    ))
                )}
            </SectionPaper>

            <GenerateReportDialog
                open={open}
                onClose={() => setOpen(false)}
                pageId={pageId}
                links={links}
                onGenerated={refetch}
            />
        </>
    )
}

function GenerateReportDialog({ open, onClose, pageId, links, onGenerated }) {
    const theme = useTheme()
    const pink = theme.palette.primary.main
    const [campaignName, setCampaignName] = useState('')
    const [focusLinkButtonId, setFocus] = useState('')
    const [rangeStart, setStart] = useState(daysAgoISO(14))
    const [rangeEnd, setEnd] = useState(todayISO())
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [created, setCreated] = useState(null)

    const reset = () => {
        setCampaignName(''); setFocus(''); setStart(daysAgoISO(14)); setEnd(todayISO())
        setError(''); setCreated(null); setBusy(false)
    }
    const close = () => { reset(); onClose() }

    const submit = async () => {
        setError('')
        if (!campaignName.trim()) { setError('Campaign name is required.'); return }
        if (rangeStart > rangeEnd) { setError('Start date must be on or before end date.'); return }
        setBusy(true)
        try {
            const report = await generateReport({
                pageId,
                campaignName: campaignName.trim(),
                focusLinkButtonId: focusLinkButtonId || null,
                rangeStart,
                rangeEnd,
            })
            setCreated(report)
            onGenerated?.()
        } catch (e) {
            setError(e?.response?.data?.error || 'Could not generate the report.')
        } finally {
            setBusy(false)
        }
    }

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>Generate brand report</DialogTitle>
            <DialogContent dividers>
                {created ? (
                    <Box>
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>Your report is ready 🎉</Typography>
                        <Typography sx={{ fontSize: '.88rem', color: '#666', mb: 1 }}>
                            View and share your report with the link below.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField value={created.shareUrl} size="small" fullWidth InputProps={{ readOnly: true }} />
                            <IconButton onClick={() => navigator.clipboard.writeText(created.shareUrl)} title="Copy">
                                <Copy size={18} />
                            </IconButton>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
                        <TextField label="Campaign name" value={campaignName}
                            onChange={(e) => setCampaignName(e.target.value)} fullWidth size="small" />
                        <Select value={focusLinkButtonId} onChange={(e) => setFocus(e.target.value)}
                            displayEmpty size="small" fullWidth>
                            <MenuItem value=""><em>No focus link</em></MenuItem>
                            {links.map((l) => <MenuItem key={l.id} value={l.id}>{l.title}</MenuItem>)}
                        </Select>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField label="From" type="date" value={rangeStart} onChange={(e) => setStart(e.target.value)}
                                size="small" fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField label="To" type="date" value={rangeEnd} onChange={(e) => setEnd(e.target.value)}
                                size="small" fullWidth InputLabelProps={{ shrink: true }} />
                        </Box>
                        {error && <Typography sx={{ color: '#b00020', fontSize: '.85rem' }}>{error}</Typography>}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button onClick={close} sx={{ textTransform: 'none' }}>{created ? 'Done' : 'Cancel'}</Button>
                {!created && (
                    <Button onClick={submit} disabled={busy} variant="contained"
                        sx={{ textTransform: 'none', background: pink, '&:hover': { background: pink, filter: 'brightness(0.92)' } }}>
                        {busy ? 'Generating…' : 'Generate'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
